import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, X, ArrowRight, Award, RotateCcw, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { api } from "@/lib/api";
import { formatUSD } from "@/lib/constants";
import { countryImage } from "@/lib/images";
import { getDisplayConfig, enabledFields } from "@/lib/displayConfig";
import { useTranslation } from "react-i18next";

const DEFAULTS = ["singapore", "unitedstates", "hongkong"];

export default function CompareCountries({ compact = false }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Admin-renamed labels stay as entered; shipped defaults are translated.
  const fLabel = (f) => (t(`cmp.f.${f.key}`, { lng: "en", defaultValue: "" }) === f.label ? t(`cmp.f.${f.key}`) : f.label);
  const cName = (c) => t(`country_name.${c.slug}`, { defaultValue: c.name });
  const [params] = useSearchParams();
  const [countries, setCountries] = useState([]);
  const [selected, setSelected] = useState([]);
  const [fields, setFields] = useState([]);

  useEffect(() => {
    api.get("/compare").then((r) => {
      const list = r.data.countries || [];
      setCountries(list);
      const fromUrl = (params.get("c") || "").split(",").map((s) => s.trim()).filter(Boolean)
        .filter((s) => list.some((c) => c.slug === s)).slice(0, 4);
      if (fromUrl.length) { setSelected(fromUrl); return; }
      const avail = DEFAULTS.filter((s) => list.some((c) => c.slug === s));
      setSelected(avail.length >= 2 ? avail : list.slice(0, 3).map((c) => c.slug));
    }).catch(() => {});
    getDisplayConfig().then((cfg) => setFields(enabledFields(cfg, "compare_country")));
  }, []);

  const bySlug = useMemo(() => Object.fromEntries(countries.map((c) => [c.slug, c])), [countries]);
  const cols = selected.map((s) => bySlug[s]).filter(Boolean);
  const remaining = countries.filter((c) => !selected.includes(c.slug));
  // "Lowest" is only meaningful across confirmed, purchasable, all-inclusive USD totals.
  // Quotation-only or unknown prices are excluded from the comparison.
  const isQuote = (c) => (c.confirmation_status || "confirmed") === "requires_confirmation";
  const eligSetup = cols.filter((c) => !isQuote(c) && c.setup_fee > 0);
  const eligAnnual = cols.filter((c) => !isQuote(c) && c.annual_fee > 0);
  const minSetup = eligSetup.length >= 2 ? Math.min(...eligSetup.map((c) => c.setup_fee)) : null;
  const minAnnual = eligAnnual.length >= 2 ? Math.min(...eligAnnual.map((c) => c.annual_fee)) : null;

  const addCountry = (slug) => { if (selected.length < 4 && !selected.includes(slug)) setSelected([...selected, slug]); };
  const removeCountry = (slug) => setSelected(selected.filter((s) => s !== slug));
  const reset = () => setSelected(DEFAULTS.filter((s) => bySlug[s]));

  const gridCols = { gridTemplateColumns: `minmax(130px,150px) repeat(${cols.length}, minmax(150px, 1fr))` };
  if (!countries.length) return null;

  const Badge = () => <span className="text-[10px] font-sans font-medium text-green-700 bg-green-100 rounded-full px-1.5 py-0.5">{t("cmp.lowest")}</span>;
  const renderVal = (c, key) => {
    if (key === "setup_fee") return isQuote(c)
      ? <span className="text-foreground">{t("cmp.customQuote")}</span>
      : (<span className="flex items-center gap-2 font-mono text-base text-foreground">{formatUSD(c.setup_fee)}
        {minSetup != null && c.setup_fee === minSetup && <Badge />}</span>);
    if (key === "annual_fee") return isQuote(c)
      ? <span className="text-foreground">{t("cmp.customQuote")}</span>
      : (<span className="flex items-center gap-2 font-mono text-foreground">{formatUSD(c.annual_fee)}<span className="text-xs text-muted-foreground">{t("cmp.perYear")}</span>
        {minAnnual != null && c.annual_fee === minAnnual && <Badge />}</span>);
    if (key === "timeline") return c.timeline ? c.timeline : t("cmp.tbc");
    if (key === "bank_count") return t(c.bank_count === 1 ? "cmp.bankOne" : "cmp.bankMany", { count: c.bank_count });
    return c[key] ? c[key] : <span className="text-muted-foreground">{t("cmp.tbc")}</span>;
  };

  return (
    <div data-testid="compare-countries">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <span className="text-sm text-muted-foreground">{t("cmp.comparing", { n: cols.length, max: 4 })}</span>
        {remaining.length > 0 && selected.length < 4 && (
          <Select value="" onValueChange={addCountry}>
            <SelectTrigger className="w-[220px] rounded-sm border-slate-300 bg-card" data-testid="compare-add-select">
              <span className="flex items-center gap-1.5 text-sm text-muted-foreground"><Plus className="h-4 w-4 text-orange-600" /> {t("cmp.addCountry")}</span>
            </SelectTrigger>
            <SelectContent>
              {remaining.map((c) => (
                <SelectItem key={c.slug} value={c.slug} data-testid={`compare-option-${c.slug}`}>{cName(c)} · {formatUSD(c.setup_fee)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <button onClick={reset} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="compare-reset"><RotateCcw className="h-3.5 w-3.5" /> {t("cmp.reset")}</button>
      </div>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="hidden lg:block overflow-x-auto rounded-2xl border border-border bg-card card-soft">
        <div className="grid border-b border-border" style={gridCols}>
          <div className="px-4 py-5 bg-secondary/40" />
          {cols.map((c) => (
            <div key={c.slug} className="relative px-4 py-5 border-l border-border" data-testid={`compare-col-${c.slug}`}>
              <button onClick={() => removeCountry(c.slug)} disabled={cols.length <= 2} className="absolute top-3 right-3 grid place-items-center h-6 w-6 rounded-full text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`compare-remove-${c.slug}`} aria-label={t("cmp.remove", { name: cName(c) })}><X className="h-3.5 w-3.5" /></button>
              <div className="h-16 w-full rounded-lg overflow-hidden mb-3">{c.flag && c.flag.length <= 4 ? <div className="h-full w-full grid place-items-center text-4xl bg-secondary/40">{c.flag}</div> : <img src={c.flag || countryImage(c.slug)} alt={c.name} className="w-full h-full object-cover" />}</div>
              <p className="font-heading text-lg text-primary leading-tight">{cName(c)}</p>
              {c.popular && <span className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-orange-700 bg-orange-100 rounded-full px-2 py-0.5"><Award className="h-3 w-3" /> {t("cmp.popular")}</span>}
            </div>
          ))}
        </div>

        {fields.map((f) => (
          <div key={f.key} className="grid border-b border-border" style={gridCols} role="row">
            <div className="px-4 py-4 text-sm text-muted-foreground flex items-center gap-2 bg-secondary/40">{fLabel(f)}</div>
            {cols.map((c) => (<div key={c.slug} className="px-4 py-4 text-sm text-foreground border-l border-border" data-testid={`compare-cell-${c.slug}-${f.key}`}>{renderVal(c, f.key)}</div>))}
          </div>
        ))}

        <div className="grid" style={gridCols}>
          <div className="px-4 py-5 bg-secondary/40" />
          {cols.map((c) => (
            <div key={c.slug} className="px-4 py-5 border-l border-border space-y-2">
              <Button onClick={() => navigate(`/start?pkg=inc_${c.slug}`)} className="w-full rounded-sm bg-orange-500 text-white hover:bg-orange-600 h-9 text-sm" data-testid={`compare-start-${c.slug}`}>{t("cmp.start")} <ArrowRight className="h-3.5 w-3.5 ml-1" /></Button>
              <Button variant="outline" onClick={() => navigate(`/company-formation/${c.slug}`)} className="w-full rounded-sm border-slate-300 bg-card hover:bg-secondary h-9 text-sm" data-testid={`compare-details-${c.slug}`}>{t("cmp.viewDetails")}</Button>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Mobile: readable stacked cards instead of a cramped wide table */}
      <div className="lg:hidden space-y-4" data-testid="compare-mobile">
        {cols.map((c) => (
          <div key={c.slug} className="rounded-2xl border border-border bg-card card-soft overflow-hidden" data-testid={`compare-card-${c.slug}`}>
            <div className="flex items-center justify-between gap-3 p-4 border-b border-border bg-secondary/30">
              <div className="flex items-center gap-3 min-w-0">
                <span className="grid place-items-center h-9 w-9 rounded-sm bg-card text-orange-600 shrink-0">{c.flag && c.flag.length <= 4 ? <span className="text-xl">{c.flag}</span> : <MapPin className="h-4 w-4" />}</span>
                <div className="min-w-0"><p className="font-heading text-base text-primary truncate">{cName(c)}</p>{c.popular && <span className="text-[10px] font-medium text-orange-700">{t("cmp.popular")}</span>}</div>
              </div>
              <button onClick={() => removeCountry(c.slug)} disabled={cols.length <= 2} className="grid place-items-center h-7 w-7 rounded-full text-muted-foreground hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed shrink-0" aria-label={t("cmp.remove", { name: cName(c) })} data-testid={`compare-card-remove-${c.slug}`}><X className="h-4 w-4" /></button>
            </div>
            <dl className="divide-y divide-border">
              {fields.map((f) => (
                <div key={f.key} className="flex items-start justify-between gap-4 px-4 py-2.5">
                  <dt className="text-xs text-muted-foreground shrink-0">{fLabel(f)}</dt>
                  <dd className="text-sm text-foreground text-right min-w-0" data-testid={`compare-mcell-${c.slug}-${f.key}`}>{renderVal(c, f.key)}</dd>
                </div>
              ))}
            </dl>
            <div className="p-4 flex gap-2">
              <Button onClick={() => navigate(`/start?pkg=inc_${c.slug}`)} className="flex-1 rounded-sm bg-orange-500 text-white hover:bg-orange-600 h-9 text-sm" data-testid={`compare-mstart-${c.slug}`}>{t("cmp.start")} <ArrowRight className="h-3.5 w-3.5 ml-1" /></Button>
              <Button variant="outline" onClick={() => navigate(`/company-formation/${c.slug}`)} className="flex-1 rounded-sm border-slate-300 bg-card hover:bg-secondary h-9 text-sm" data-testid={`compare-mdetails-${c.slug}`}>{t("cmp.details")}</Button>
            </div>
          </div>
        ))}
      </div>

      <p className="mt-4 text-[11px] text-muted-foreground" data-testid="compare-scope-note">{t("cmp.scopeNote")}</p>

      {compact && (
        <div className="mt-6 text-center">
          <Button variant="outline" onClick={() => navigate("/compare")} className="rounded-sm border-slate-300 bg-card hover:bg-secondary" data-testid="compare-see-full">{t("cmp.openFull")} <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
        </div>
      )}
    </div>
  );
}
